import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://www.crownsatanduniversity.com";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      "url": siteUrl,
      "name": "Crown SAT & University",
      "description": "Expert SAT/ACT test prep, 1-on-1 tutoring, essay coaching, and Ivy League admissions consulting for students in the USA and Europe.",
      "publisher": { "@id": `${siteUrl}/#organization` },
      "inLanguage": "en-US"
    },
    {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      "name": "Crown SAT & University",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`,
        "width": 200,
        "height": 60
      },
      "sameAs": [],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": ["English"]
      }
    },
    {
      "@type": "WebPage",
      "@id": `${siteUrl}/#webpage`,
      "url": siteUrl,
      "name": "Crown SAT & University | SAT ACT Prep & Ivy League Admissions Consulting USA & Europe",
      "description": "Crown SAT & University — expert SAT/ACT test prep, 1-on-1 tutoring, essay coaching, and Ivy League admissions consulting for students in the USA and Europe. 98% acceptance rate.",
      "isPartOf": { "@id": `${siteUrl}/#website` },
      "about": { "@id": `${siteUrl}/#organization` },
      "inLanguage": "en-US",
      "dateModified": "2026-04-11"
    },
    {
      "@type": "EducationalOrganization",
      "@id": `${siteUrl}/#edu-org`,
      "name": "Crown SAT & University",
      "url": siteUrl,
      "description": "Crown SAT & University provides expert SAT/ACT test prep, Ivy League admissions consulting, essay coaching, and 1-on-1 tutoring for students in the USA and Europe.",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "College Prep Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Course",
              "name": "SAT Prep Course",
              "description": "Comprehensive SAT preparation with expert tutors targeting 1550+ scores."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Course",
              "name": "ACT Prep Course",
              "description": "Full ACT preparation program with personalized study plans."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Ivy League Admissions Consulting",
              "description": "End-to-end admissions strategy for top universities including Harvard, MIT, and Stanford."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "College Essay Coaching",
              "description": "Expert guidance on crafting compelling college application essays."
            }
          }
        ]
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What SAT score improvement can I expect?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our students average a 200+ point improvement on the SAT. Our expert tutors use proven strategies and personalized study plans to maximize your score."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer admissions consulting for European universities?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Crown SAT & University provides admissions consulting for top universities in both the USA and Europe, including Oxbridge, LSE, and other leading institutions."
          }
        },
        {
          "@type": "Question",
          "name": "How does the free consultation work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Book a free 30-minute consultation with one of our expert advisors. We will assess your current academic profile, discuss your target schools, and outline a personalized roadmap to success."
          }
        },
        {
          "@type": "Question",
          "name": "What is your acceptance rate to top universities?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We maintain a 98% acceptance rate to students' first or second choice universities, with hundreds of students admitted to Ivy League and top-ranked schools each year."
          }
        }
      ]
    }
  ]
};

const communityJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${siteUrl}/community#webpage`,
      "url": `${siteUrl}/community`,
      "name": "Community Hub | Crown SAT & University — AI-Matched Tutors, Students & Parents",
      "description": "Browse AI-matched students, teachers, and parents on Crown SAT & University. Connect via WhatsApp or email, share resources, and build your academic support network.",
      "isPartOf": { "@id": `${siteUrl}/#website` },
      "inLanguage": "en-US",
      "dateModified": "2026-04-11"
    },
    {
      "@type": "CommunityForum",
      "name": "Crown SAT Community Hub",
      "url": `${siteUrl}/community`,
      "description": "An AI-powered community connecting students, teachers, and parents for academic mentorship, SAT prep support, and university admissions guidance.",
      "provider": { "@id": `${siteUrl}/#organization` }
    }
  ]
};

const donateJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${siteUrl}/donate#webpage`,
      "url": `${siteUrl}/donate`,
      "name": "Donate | Crown SAT & University — Help Village Students Get Books & Supplies",
      "description": "Donate to Crown SAT & University's Village & City Bridge program. Your small donation buys books, pens, and clothes for village students worldwide. Every gift tracked and shared.",
      "isPartOf": { "@id": `${siteUrl}/#website` },
      "inLanguage": "en-US",
      "dateModified": "2026-04-11"
    },
    {
      "@type": "DonateAction",
      "name": "Donate to Village Students",
      "description": "Support village students by donating books, stationery, and clothing through Crown SAT & University's Village & City Bridge program.",
      "recipient": {
        "@type": "EducationalOrganization",
        "name": "Crown SAT & University",
        "url": siteUrl
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Where do donations go?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "100% of donations go directly to purchasing books, stationery, and clothing for village students in rural areas worldwide. Every gift is tracked and publicly listed."
          }
        },
        {
          "@type": "Question",
          "name": "Can I donate anonymously?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, you can choose to donate anonymously. Your name will appear as 'Anonymous' on the public donation wall."
          }
        }
      ]
    }
  ]
};

const farmJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${siteUrl}/farm#webpage`,
      "url": `${siteUrl}/farm`,
      "name": "Farm Reward System | Crown SAT & University — Grow Vegetables by Studying",
      "description": "Earn study points and grow a virtual farm on Crown SAT & University. The more you study, the more vegetables you unlock. A fun gamified reward system for students.",
      "isPartOf": { "@id": `${siteUrl}/#website` },
      "inLanguage": "en-US",
      "dateModified": "2026-04-11"
    },
    {
      "@type": "Game",
      "name": "Crown SAT Virtual Farm",
      "description": "A gamified study reward system where students earn points by studying and use them to grow virtual vegetables on their farm.",
      "url": `${siteUrl}/farm`,
      "provider": { "@id": `${siteUrl}/#organization` }
    }
  ]
};

const PAGE_JSON_LD: Record<string, object> = {
  "/": homeJsonLd,
  "/community": communityJsonLd,
  "/donate": donateJsonLd,
  "/farm": farmJsonLd,
};

export default function SeoJsonLd() {
  const { pathname } = useLocation();

  useEffect(() => {
    const scriptId = "page-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const jsonLd = PAGE_JSON_LD[pathname];
    if (!jsonLd) return;

    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [pathname]);

  return null;
}
